// The seat's side panel on the org chart (CHART-DIMENSIONS, CHART-LENSES, CHART-KB): a click on
// a card opens it beside the canvas with the holder and title, the seat-setup ring, the facts
// the chart knows (reports to, direct reports, teams, authority and delegation lines), then
// EVERY lens the viewer may see on this seat — the lens's score in its band, its rows as
// `GET org-chart/dimensions` answered them, and a mini trend where history exists (Performance:
// the last six weeks' KPI scores from the seat scorecard; Finance: the last four months' budget
// used from budget vs actual; both read only while the panel is open). CHART-KB adds:
//   - Completeness: the missing setup items, each with its "Add …" action (assign a holder,
//     set reports to, add to a team, add a responsibility, a measurable, a budget, a priority);
//     items in a gated dimension the viewer may not see are counted, not named;
//   - Keystones: the quarter's priorities with their milestones, read from `GET seats/{id}/goals`
//     while the panel is open; a manager or the seat's holder adds one (with milestones), marks
//     a milestone done, sets the track, or removes it (`goals.*`);
//   - Execution: the goal score and each measurable's actual against its target.
// A lens the server left out shows who it is shown to instead, so a member understands the
// chart rather than wondering what is missing. The seat page remains one click away.
// CHART-PANEL (founder review 2026-10-21): the panel is 440 px and resizable from its left edge
// (the width remembered per user); its header picks what it shows — "All dimensions" (the
// stack), one lens (only that section, expanded: every measurable of the week, every
// responsibility, budget / actual / left, the six parts as bars), or "Follow chart tab" — with
// a strip in the lenses' hues and ‹ › (and ←/→ while the panel has focus) to step through
// them, and a pin that keeps the mode across seats and visits (`seat-panel-mode.ts`). "Seat
// setup" is drawn only while the seat is not fully set up; the header's ring carries the %.
import {
  bandOf,
  type CompletenessItem,
  type Lens,
  type LensThresholds,
  lensGoodness,
  SCORED_LENSES,
  type ScoredLens,
} from "@companyos/domain-work/org";
import { ApiError } from "@companyos/sdk";
import {
  Badge,
  Button,
  buttonVariants,
  Checkbox,
  CompletenessRing,
  cn,
  InfoTip,
  InlineBar,
  LensBandDot,
  LensScore,
  NativeSelect,
  NativeSelectOption,
  Popover,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
  Sparkline,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import {
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ArrowUpRightIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CrosshairIcon,
  GitBranchPlusIcon,
  LockIcon,
  PencilIcon,
  PinIcon,
  PlusIcon,
  SendIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import {
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { Api } from "../../api/client";
import type { Goal, ScorecardMetric } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { shiftWeek, weekRangeLabel } from "../../focus/weeks";
import { AgentReportMark } from "../agent-report";
import { DeBudgetPaceRow } from "../agents/budget-pace";
import { BudgetCreateDialog } from "../agents/budgets-screen";
import { type CompanyAccess, isManager } from "../company";
import { budgetState } from "../finance/budget-vs-actual";
import { monthLabel, PayHiddenBadge, ratioText } from "../finance/finance-parts";
import { budgetVsActualQuery } from "../finance/finance-queries";
import { OpenSeatSetup, SendSeatSetupDialog } from "../flows/flow-entry";
import { formatCardMetric, formatMoney, formatScore, seatHolderLabel } from "../format";
import { HEALTH_BAR, HEALTH_LABEL, HEALTH_TEXT, lineHealth } from "../kpi/health";
import { kpiKeys, seatSetQuery } from "../kpi/queries";
import { AddKpiDialog, holdersOf } from "../kpi/seat-kpis";
import { myNumbersQuery, responsibilitiesQuery, seatScorecardQuery } from "../queries";
import { ReportingStatusChip, reportingProps } from "../reporting/reporting-status";
import { RowAction, RowActions } from "../settings/row-actions";
import { SEAT_STATUS_LABELS } from "../structure/labels";
import { ResponsibilityDialog } from "../structure/responsibilities";
import { SEAT_STATUS_TONE } from "../structure/seats-screen";
import { useFeature, useSession } from "../use-feature";
import { SeatPlaybooks } from "../work/playbook";
import { TaskDrawer } from "../work/task-drawer";
import {
  type Dimension,
  type GatedDimension,
  hoursText,
  LEVEL_LABELS,
  type Level,
  type SeatDimensions,
} from "./chart-dimensions";
import {
  LENS_HINTS,
  LENS_LABELS,
  LENS_UNITS,
  lensAudience,
  lensScoreOf,
  lensVisible,
} from "./chart-lenses";
import {
  type ChartSeat,
  COMPOSITION_LABELS,
  LINE_VERBS,
  type Line,
  type OrgChart,
  seatComposition,
} from "./chart-model";
import { holderNames, SeatAvatar, seatNameText } from "./chart-parts";
import { chartKeys } from "./chart-queries";
import { SeatDeFill } from "./de-seat-owner";
import { EditSeatDrawer } from "./edit-seat-drawer";
import {
  COMPLETENESS_ACTIONS,
  COMPLETENESS_LABELS,
  TRACK_BAND,
  TRACK_LABELS,
  TRACKS,
  type Track,
} from "./keystones";
import { PriorityDetailModal, PriorityDialog } from "./priority-dialog";
import { SeatBoardsLine } from "./seat-boards";
import { FinanceLinks, SeatBudgets, SeatSpend } from "./seat-finance";
import { DefinitionLine, SeatTitle } from "./seat-header";
import {
  GoalPicker,
  KpiDetailModal,
  ResponsibilityItemModal,
  type ResponsibilitySummary,
} from "./seat-item-modals";
import { ConfirmRemove, ItemRow, MoreRowsLink, TabAdd, TabEmpty } from "./seat-item-row";
import {
  clampPanelWidth,
  cyclePanel,
  isPanelLens,
  PANEL_LENS_FULL,
  PANEL_LENS_SHORT,
  PANEL_LENSES,
  PANEL_WIDTH,
  type PanelLens,
  type PanelMode,
  type PanelShown,
  readPanelWidth,
  resolvePanelMode,
  showSeatSetup,
  usePanelMode,
  writePanelWidth,
} from "./seat-panel-mode";
import { SeatRecord } from "./seat-record";
import {
  failureRows,
  goalCandidates,
  goalRows,
  kpiRows,
  recordRows,
  scoredRecordCount,
  topOf,
  weightChip,
} from "./seat-rows";
import {
  ExecutionModal,
  OverallModal,
  type PanelSection,
  PerformanceModal,
  PrioritiesModal,
  ResponsibilitiesModal,
  ScorecardLink,
  SetupChecklist,
  seatTasksKey,
  TasksModal,
} from "./seat-section-modals";
import { SeatNewTask, SeatTaskRows } from "./seat-tasks";
import { PageArrow, SectionGear } from "./section-modal";
import { ServesLine } from "./shared-des";
import { TeamMemberDialog } from "./team-member-dialog";

/** SEAT-DRAWER-CLICKS: what a drawer row opens in place. */
type PanelItem =
  | {
      kind: "measurable";
      id: string;
      name: string;
      /** Opened from the row's ✎: the KPI editor opens over the modal. */
      edit?: boolean;
      /** The tab the row is on (Execution: the target is what the row is about). */
      home?: "performance" | "execution" | "record";
    }
  | { kind: "responsibility"; id: string; summary: ResponsibilitySummary }
  | { kind: "priority"; goal: Goal };

/** A drawer row that opens its item in place: the row's look, a button's behaviour. */
const ROW_BUTTON =
  "-mx-1 flex w-[calc(100%+0.5rem)] min-w-0 items-center gap-2 rounded-sm px-1 py-0.5 text-left hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring";
/** A name in the facts that opens a seat's drawer or a popover in place. */
const NAME_BUTTON =
  "rounded-sm text-left underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-ring";

type TaskWatch = { release: () => void };

const RECORD_HINT =
  "The digital employee's track record over four weeks, counted from real work: approvals, completed tasks, take-overs, cost and supervision time.";

/** "1 of 2": the rows with a target that met it, of the rows with a target (unreported count). */
function metOf(rows: readonly Pick<ScorecardMetric, "attainment" | "target">[]): string {
  const targeted = rows.filter((m) => m.target !== null);
  return `${targeted.filter((m) => (m.attainment ?? 0) >= 1).length} of ${targeted.length}`;
}

/** The mean attainment of the rows that have one ("—" when none does). */
function meanAttainment(rows: readonly Pick<ScorecardMetric, "attainment">[]): string {
  const a = rows.map((m) => m.attainment).filter((v): v is number => v !== null);
  return a.length ? `${Math.round((a.reduce((s, v) => s + v, 0) / a.length) * 100)}%` : "—";
}

/**
 * SEAT-DRAWER-CLICKS: while Work's task drawer is open over the chart, each write that lands
 * refreshes the chart's reads and the seat's task list. Released (the drawer closed, or the panel
 * gone), it keeps watching only until no write is pending, so a save still in flight at close
 * refreshes the counts when it lands.
 */
function watchTaskWrites(qc: QueryClient, co: string, seatId: string): TaskWatch {
  let released = false;
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: chartKeys.orgChart(co) });
    void qc.invalidateQueries({ queryKey: seatTasksKey(co, seatId) });
  };
  const pending = () =>
    qc
      .getMutationCache()
      .getAll()
      .some((m) => m.state.status === "pending");
  const stop = qc.getMutationCache().subscribe((e) => {
    if (e.type !== "updated") return;
    if (e.action.type === "success") refresh();
    if (released && (e.action.type === "success" || e.action.type === "error") && !pending())
      stop();
  });
  return {
    release: () => {
      if (released) return;
      released = true;
      if (!pending()) stop();
    },
  };
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;
/** Weeks of Performance history and months of Finance history the panel reads. */
const TREND_WEEKS = 6;
const TREND_MONTHS = 4;

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 gap-3 text-sm leading-5">
      <dt className="w-24 shrink-0 text-xs leading-5 text-faint">{label}</dt>
      <dd className="min-w-0 flex-1 text-secondary">{children}</dd>
    </div>
  );
}

/** A lens's section: its hue as a 3 px rule on the left, the title, the score on the right. */
function LensSection({
  lens,
  title,
  score,
  current,
  gear,
  period,
  add,
  children,
}: {
  /** A lens, or the DE seat's Record (a drawer tab, not a chart lens). */
  lens: Lens | "record";
  title: string;
  score?: ReactNode;
  /** The lens the chart is on: marked so the eye lands on it. */
  current: boolean;
  /** SEAT-DRAWER-MODALS: the section's gear (absent where the viewer may not change it). */
  gear?: ReactNode;
  /** SEAT-DRAWER-UX §2: the period the rows are for (the week, the month, the quarter). */
  period?: ReactNode;
  /** SEAT-DRAWER-UX §2: the tab's Add, always visible to a viewer who may add. */
  add?: ReactNode;
  children: ReactNode;
}) {
  const hint = lens === "record" ? RECORD_HINT : LENS_HINTS[lens];
  return (
    <section
      data-slot="panel-lens"
      data-lens={lens}
      data-current={current ? "" : undefined}
      className={cn(
        "group/section flex flex-col gap-1.5 border-t border-subtle pt-3 pl-2.5",
        "border-l-[3px] border-l-(--lens)",
        current && "bg-sunken/60 -mr-1 rounded-r-md pr-1 pb-2",
      )}
    >
      <div className="flex min-h-6 items-center gap-1">
        <h3
          className="text-[11px] leading-4 font-medium tracking-wide text-faint uppercase"
          title={hint}
        >
          {title}
        </h3>
        <InfoTip label={`About ${title.toLowerCase()}`} className="size-4">
          {hint}
        </InfoTip>
        {gear}
        {score !== undefined ? <span className="ml-auto">{score}</span> : null}
      </div>
      {period !== undefined || add ? (
        <div className="-mt-1 flex min-h-6 items-center gap-2" data-slot="tab-header">
          {period !== undefined ? (
            <span className="text-xs text-muted-foreground" data-slot="tab-period">
              {period}
            </span>
          ) : null}
          {add ? <span className="ml-auto">{add}</span> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/** "Shown to the reporting line": what a hidden lens says instead of its value. */
function ShownTo({ level }: { level: Level | null }) {
  return (
    <p className="flex items-center gap-1.5 text-xs text-faint" data-slot="shown-to">
      <LockIcon aria-hidden="true" className="size-3" />
      Shown to: {level ? LEVEL_LABELS[level].toLowerCase() : "nobody"}.
    </p>
  );
}

/** "2026-09" → the month `delta` months away. */
function shiftMonth(key: string, delta: number): string {
  const [y, m] = key.split("-").map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return d.toISOString().slice(0, 7);
}

export const goalsKey = (co: string, seatId: string, quarter: string | null) =>
  [...chartKeys.orgChart(co), "goals", seatId, quarter ?? ""] as const;

export function SeatPanel({
  api,
  company,
  seat,
  chart,
  sup,
  roleName,
  teams,
  lines,
  titleOf,
  dims,
  pending,
  served,
  visibility,
  week,
  month,
  quarter,
  lens,
  lenses,
  thresholds,
  focused,
  onFocus,
  onLines,
  onReportsTo,
  onClose,
  onOpenSeat,
  required,
}: {
  api: Api;
  company: CompanyAccess;
  seat: ChartSeat;
  chart: OrgChart | undefined;
  sup: ReadonlyMap<string, string | null>;
  roleName: string;
  teams: readonly string[];
  lines: readonly Line[];
  titleOf: (id: string) => string;
  dims: SeatDimensions | undefined;
  pending: boolean;
  served: readonly Dimension[];
  visibility: Record<GatedDimension, Level>;
  week: string;
  /** The month the dimensions were read for (null until they arrive). */
  month: string | null;
  /** CHART-KB: the quarter the keystones are read for (null until the dimensions arrive). */
  quarter: string | null;
  /** The lens the chart is on. */
  lens: Lens;
  /** The lenses the company serves. */
  lenses: readonly Lens[];
  thresholds: LensThresholds;
  focused: boolean;
  onFocus: () => void;
  /** A manager's way to the seat's Lines dialog (the "Set reports to" action). */
  onLines?: (() => void) | undefined;
  /** STRUCTURE-CHANGED §3.1: a manager's "Reports to…" picker (stages a move in the draft). */
  onReportsTo?: (() => void) | undefined;
  onClose: () => void;
  /** SEAT-DRAWER-CLICKS: open another seat's drawer in place (reports to, direct reports, lines). */
  onOpenSeat?: ((id: string) => void) | undefined;
  /** SEAT-DRAWER-MODALS: the company's completeness rules (the Seat setup modal's checklist). */
  required?: Partial<Record<CompletenessItem, boolean>> | undefined;
}) {
  const qc = useQueryClient();
  const { me } = useSession();
  const id = useId();
  const manager = isManager(company);
  const mine = me.memberships
    .filter((m) => m.company_id === company.id)
    .map((m) => m.membership_id);
  const holder = seat.holders.some((h) => mine.includes(h.membership_id));
  /** 05 `goal` editor: a manager, or the seat's holder. */
  const canWriteGoals = manager || holder;
  const composition = seatComposition(seat);
  const supervisorId = sup.get(seat.id) ?? null;
  const supervisor = supervisorId ? chart?.seats.find((s) => s.id === supervisorId) : undefined;
  const reports = [...sup].filter(([, p]) => p === seat.id).length;
  const outgoing = lines.filter((l) => l.from === seat.id);
  const incoming = lines.filter((l) => l.to === seat.id);
  const waiting = <p className="text-xs text-faint">…</p>;
  const visible = (l: Lens) => !pending && lensVisible(l, dims);
  const scoreMark = (l: Lens) => {
    if (!visible(l)) return undefined;
    const sc = lensScoreOf(l, seat, dims, thresholds);
    return (
      <LensScore
        size="md"
        score={sc.score}
        band={sc.band}
        unit={LENS_UNITS[l]}
        {...(sc.note ? { note: sc.note } : {})}
        data-lens-score={l}
      />
    );
  };

  // CHART-PANEL: what the panel shows. The lenses offered are the ones the company serves and
  // the server sent for this seat (while the dimensions load, every served one).
  const view = usePanelMode(me.user.id, seat.id);
  /** The DE holding the seat (its definition line and its Record tab). */
  const deHolder = seat.holders.find((h) => h.membership_kind === "digital_employee");
  /**
   * The viewer may read the seat itself (`seat#can_view`, services/api auth/can.ts): a company
   * manager, the seat's holder, the holder of its direct supervisor seat, or (a seat a DE holds)
   * a person holding any seat on its reporting chain above. The seat's spend and its DE's record
   * are this read (a KPI-visibility grant shows the KPIs only).
   */
  const seatViewer = (() => {
    if (manager || holder) return true;
    const held = new Set(
      (chart?.seats ?? [])
        .filter((s) => s.holders.some((h) => mine.includes(h.membership_id)))
        .map((s) => s.id),
    );
    const direct = sup.get(seat.id);
    if (direct && held.has(direct)) return true;
    if (!seat.holders.some((h) => h.membership_kind === "digital_employee")) return false;
    const seen = new Set<string>([seat.id]);
    for (let up = direct; up && !seen.has(up); up = sup.get(up)) {
      if (held.has(up)) return true;
      seen.add(up);
    }
    return false;
  })();
  // SEAT-DRAWER-UX §2.6: Record, on a DE-held seat, for a seat viewer who sees its KPIs.
  const recordOn =
    deHolder !== undefined &&
    seatViewer &&
    lenses.includes("performance") &&
    (pending || dims?.kpi !== undefined);
  const available: PanelLens[] = PANEL_LENSES.filter((l) =>
    l === "record"
      ? recordOn
      : lenses.includes(l) &&
        (l !== "finance" || served.includes("cost")) &&
        (pending || lensVisible(l, dims)),
  );
  const shown = resolvePanelMode(view.mode, lens, available);
  const one = shown !== "all";
  const shows = (l: PanelLens) => shown === "all" || shown === l;
  const current = (l: Lens) => !one && lens === l;
  const step = (dir: 1 | -1) => view.setMode(cyclePanel(shown, available, dir));
  /** A pinned lens this seat does not show the viewer: the panel says why it shows everything. */
  const fallback = isPanelLens(view.mode) && shown === "all" && !pending ? view.mode : null;

  // CHART-PANEL: the width, dragged from the left edge (pointer) or stepped (←/→ on the edge).
  const [width, setWidth] = useState(() =>
    clampPanelWidth(readPanelWidth(me.user.id), window.innerWidth),
  );
  const widthRef = useRef(width);
  widthRef.current = width;
  // The width reaches the panel as a custom property (no style props outside packages/ui).
  const asideRef = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    asideRef.current?.style.setProperty("--seat-panel-w", `${width}px`);
  }, [width]);
  const drag = useRef<{ x: number; w: number } | null>(null);
  const resizeTo = (w: number) => setWidth(clampPanelWidth(w, window.innerWidth));
  const onDragStart = (e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = { x: e.clientX, w: widthRef.current };
  };
  const onDragMove = (e: PointerEvent<HTMLDivElement>) => {
    if (drag.current) resizeTo(drag.current.w + drag.current.x - e.clientX);
  };
  const onDragEnd = () => {
    if (!drag.current) return;
    drag.current = null;
    writePanelWidth(me.user.id, widthRef.current);
  };
  const onEdgeKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const by = e.key === "ArrowLeft" ? 24 : e.key === "ArrowRight" ? -24 : 0;
    if (!by) return;
    e.preventDefault();
    e.stopPropagation();
    const next = clampPanelWidth(widthRef.current + by, window.innerWidth);
    setWidth(next);
    writePanelWidth(me.user.id, next);
  };
  /** ←/→ anywhere in the panel but a field steps through the dimensions. */
  const onPanelKey = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || e.defaultPrevented) return;
    const t = e.target as HTMLElement;
    if (t.closest("input, textarea, select, [contenteditable='true'], [role='separator']")) return;
    // A modal opened from the panel is portalled out of it; its keys are its own.
    if (t.closest("[role='dialog'], [role='alertdialog']")) return;
    e.preventDefault();
    step(e.key === "ArrowLeft" ? -1 : 1);
  };

  // Trends: the weeks before the focused week (Performance) and the months before the
  // dimensions' month (Finance), read only when the lens is visible on this seat.
  const perfWeeks =
    (visible("performance") || visible("execution")) && dims?.kpi
      ? Array.from({ length: TREND_WEEKS }, (_, i) => shiftWeek(week, i - TREND_WEEKS + 1))
      : [];
  const perfTrend = useQueries({
    queries: perfWeeks.map((w) => ({
      ...seatScorecardQuery(api, company.id, seat.id, w),
      staleTime: 60_000,
    })),
  });
  const finMonths =
    visible("finance") && dims?.cost?.budget_minor != null && month
      ? Array.from({ length: TREND_MONTHS }, (_, i) => shiftMonth(month, i - TREND_MONTHS + 1))
      : [];
  const finTrend = useQueries({
    queries: finMonths.map((m) => ({
      ...budgetVsActualQuery(api, company.id, "month", m),
      staleTime: 60_000,
    })),
  });
  const perfValues = perfTrend.map((q) =>
    q.data && q.data.metrics.length > 0 && q.data.kpi_score !== null
      ? Math.round(q.data.kpi_score)
      : null,
  );
  const finValues = finTrend.map((q) => {
    const n = q.data?.nodes.find((x) => x.kind === "seat" && x.id === seat.id);
    return n && n.utilization !== null ? Math.round(Number(n.utilization) * 100) : null;
  });
  const trendOf = (values: (number | null)[], labels: string[], unit: string) => {
    const present = values.filter((v) => v !== null).length;
    if (present < 2) return null;
    const text = values.map((v, i) => `${labels[i]}: ${v === null ? "—" : `${v}${unit}`}`);
    return (
      <div
        className={cn("flex items-center gap-2", one && "flex-col items-stretch gap-1")}
        data-slot="lens-trend"
      >
        <Sparkline
          values={values}
          label={`Last ${values.length}: ${text.join(", ")}`}
          className={cn("shrink-0 text-(--lens)", one ? "h-12 w-full" : "h-6 w-24")}
        />
        <span className="text-xs text-faint">
          {one ? `${labels[0]} – ${labels[labels.length - 1]} · ` : ""}
          {present} of {values.length} periods
        </span>
      </div>
    );
  };
  /** The focused week's scorecard (the trend's last read): every measurable, for the one-lens view. */
  const weekCard = perfTrend[perfTrend.length - 1]?.data;
  // SEAT-DRAWER-UX §1.2: the week's rows split by weight and key (null while the week loads).
  const kpiList = weekCard ? kpiRows(weekCard.metrics) : null;
  const goalList = weekCard ? goalRows(weekCard.metrics) : null;
  const recordCounted = weekCard ? scoredRecordCount(weekCard.metrics) : 0;
  // FAILURE-STOP: the failure KPIs (weight 0) under Performance too.
  const failureList = weekCard ? failureRows(weekCard.metrics) : [];
  // Responsibilities alone: every one of them (the card and the stack show the heaviest few).
  const allResponsibilities = useInfiniteQuery({
    ...responsibilitiesQuery(api, company.id, seat.id),
    // A seat viewer's read (a manager, the holder): anyone else reads the dimensions' top five.
    enabled:
      shown === "responsibilities" &&
      (manager || holder) &&
      (dims?.responsibilities?.count ?? 0) > 0,
  });
  // Every page (the tab lists them all), heaviest first.
  // A failed page stops the reading (no retry loop); the list then shows what it has.
  const {
    hasNextPage: respMore,
    isFetchingNextPage: respFetching,
    isFetchNextPageError: respFailed,
  } = allResponsibilities;
  const fetchNextResp = allResponsibilities.fetchNextPage;
  useEffect(() => {
    if (respMore && !respFetching && !respFailed) void fetchNextResp();
  }, [respMore, respFetching, respFailed, fetchNextResp]);
  const responsibilityRows = allResponsibilities.data?.pages
    .flatMap((p) => p.data)
    .filter((r) => r.status === "active")
    .sort((a, b) => Number(b.weight) - Number(a.weight));

  // CHART-KB keystones: the quarter's priorities, read while the panel is open and the lens
  // is visible on the seat; every write puts the chart's dimensions back in question.
  const keystonesOn = visible("keystones") && quarter !== null;
  const goals = useQuery({
    queryKey: goalsKey(company.id, seat.id, quarter),
    queryFn: () =>
      api.client.goals.list({
        params: { co: company.id, id: seat.id },
        query: { kind: "priority", period_key: quarter ?? "" },
      }),
    enabled: keystonesOn,
    staleTime: 30_000,
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: chartKeys.orgChart(company.id) });
  };
  // KPI-ASSIGN: "Add a measurable" opens the KPI editor here (the seat page's KPIs tab otherwise).
  // (Writable only: in the add-on's grace period the seat page's KPIs tab reads them.)
  const kpiFeature = useFeature("kpi");
  const kpiOn = kpiFeature.enabled && !kpiFeature.readOnly;
  // SEAT-DRAWER-MODALS: a holder (no manager) configures Performance only for a seat the server
  // lets it report on (its self-report scope, `me/numbers`), read only for such a holder.
  const selfReports = useQuery({
    ...myNumbersQuery(api, company.id),
    enabled: kpiOn && holder && !manager,
  });
  // GUIDED-FLOW: the Send seat setup dialog.
  const [sendingSetup, setSendingSetup] = useState(false);
  // A new KPI, or (Execution's Add goal) a new goal: goal weight 1, KPI weight 0.
  const [addingKpi, setAddingKpi] = useState<"kpi" | "goal" | null>(null);
  // SEAT-DRAWER-UX §2: who adds, edits and removes the seat's KPIs from the rows (as the seat
  // page's KPIs panel: a manager, the add-on writable, an open seat).
  const canEditKpis = manager && kpiOn && seat.status !== "closed";
  // The seat's stored set: a row's remove goes under its version (a set changed since is a 409).
  const kpiSet = useQuery({ ...seatSetQuery(api, company.id, seat.id), enabled: canEditKpis });
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null);
  const removeKpi = useMutation({
    mutationFn: (metricId: string) =>
      api.client.seats.removeMetric({
        params: { co: company.id, id: seat.id, metric_id: metricId },
        ifMatch: kpiSet.data?.version ?? 0,
      }),
    onSuccess: (saved) => {
      qc.setQueryData(kpiKeys.seatSet(company.id, seat.id), saved);
      toast.add({ title: "KPI removed from the seat", type: "success" });
      void qc.invalidateQueries({
        queryKey: ["company", company.id],
        predicate: (q) => q.queryKey[4] !== "metric-set",
      });
    },
    onError: (e) => {
      toast.add({
        title: "The KPI was not removed",
        description:
          e instanceof ApiError && (e.status === 409 || e.status === 412)
            ? "The seat's KPIs changed meanwhile. Try again on the fresh list."
            : e instanceof Error
              ? e.message
              : undefined,
        type: "error",
      });
      void qc.invalidateQueries({ queryKey: kpiKeys.seatSet(company.id, seat.id) });
    },
    onSettled: () => setRemoving(null),
  });
  // SEAT-DRAWER-UX §2.4: the Tasks tab's New task / Give work.
  const [newTask, setNewTask] = useState(false);
  // Execution's "Weight an existing KPI": the scored KPIs with no goal weight yet.
  const [pickingGoal, setPickingGoal] = useState(false);
  // SEAT-DRAWER-UX §3: the setup checklist behind the header's ring.
  const [setupOpen, setSetupOpen] = useState(false);
  // MODALS (Andrei, 2026-10-27): every other quick add opens a dialog over the chart, so the
  // panel stays open where it was.
  const [quick, setQuick] = useState<
    "responsibility" | "priority" | "team" | "holder" | "budget" | null
  >(null);
  // SEAT-DRAWER-MODALS: the section whose modal is open (its gear, or an inline action).
  const [section, setSection] = useState<PanelSection | null>(null);
  // SEAT-DRAWER-CLICKS: the item a row opened (its modal, over the chart).
  const [item, setItem] = useState<PanelItem | null>(null);
  // A task opened from the Tasks modal: Work's task drawer over the chart (the modal closed, so
  // nothing stacks over it). Every write made in it puts the chart's reads back in question.
  const [task, setTask] = useState<string | null>(null);
  // The watch outlives the task drawer (and this panel): a write still in flight when it closes
  // refreshes the chart when it lands; the watch ends once no write is pending.
  const taskWatch = useRef<TaskWatch | null>(null);
  const openTask = (taskId: string) => {
    taskWatch.current ??= watchTaskWrites(qc, company.id, seat.id);
    setTask(taskId);
  };
  const closeTask = () => {
    setTask(null);
    taskWatch.current?.release();
    taskWatch.current = null;
  };
  useEffect(
    () => () => {
      taskWatch.current?.release();
    },
    [],
  );
  /** Another seat's drawer in place of this one (the chart keys the panel by seat, so what this
   *  one had open closes with it). */
  const openSeat = onOpenSeat;
  const closed = seat.status === "closed";
  const directReports = (chart?.seats ?? []).filter((s) => sup.get(s.id) === seat.id);
  /** Membership id → display name (the KPI modal's history says who entered a value). */
  const holderNamesById = new Map(
    (chart?.seats ?? []).flatMap((s) => s.holders.map((h) => [h.membership_id, h.display_name])),
  );
  /** A seat named in the facts: its drawer, in place, when it is on the chart. */
  const seatLink = (seatId: string) =>
    openSeat && chart?.seats.some((s) => s.id === seatId) ? (
      <button
        type="button"
        className={NAME_BUTTON}
        data-slot="panel-line-seat"
        onClick={() => openSeat(seatId)}
      >
        {titleOf(seatId)}
      </button>
    ) : (
      titleOf(seatId)
    );
  /** Who may open each section's modal: the gear is drawn only for them. */
  const canConfigure: Record<PanelSection, boolean> = {
    overall: manager,
    performance:
      kpiOn &&
      dims?.kpi !== undefined &&
      (manager || selfReports.data?.seats.some((s) => s.seat_id === seat.id) === true),
    execution: manager && kpiOn && dims?.kpi !== undefined,
    responsibilities: manager && !closed,
    // The seat's own tasks: a manager (every board) or the seat's holder (its own work).
    tasks: (manager || holder) && dims?.load !== undefined,
    finance: manager && dims?.cost !== undefined,
    keystones: canWriteGoals && dims?.keystones !== undefined,
  };
  const gearFor = (s: PanelSection, name: string) =>
    !pending && canConfigure[s] ? (
      <SectionGear section={name} onClick={() => setSection(s)} />
    ) : undefined;
  const [goalError, setGoalError] = useState<string | null>(null);
  const patch = useMutation({
    mutationFn: (v: { id: string; body: Parameters<typeof api.client.goals.update>[0]["body"] }) =>
      api.client.goals.update({ params: { co: company.id, id: v.id }, body: v.body }),
    onSuccess: refresh,
    onError: (e) => setGoalError(e instanceof Error ? e.message : "Could not save the change."),
  });
  const remove = useMutation({
    mutationFn: (goalId: string) =>
      api.client.goals.delete({ params: { co: company.id, id: goalId } }),
    onSuccess: refresh,
  });
  const toggleMilestone = (g: Goal, milestoneId: string, done: boolean) =>
    patch.mutate({
      id: g.id,
      body: {
        milestones: g.milestones.map((m) => ({
          id: m.id,
          title: m.title,
          ...(m.due_at ? { due_at: m.due_at } : {}),
          done: m.id === milestoneId ? done : m.done_at !== null,
        })),
      },
    });
  const priorityRow = (g: Goal) => {
    const done = g.milestones.filter((m) => m.done_at !== null).length;
    return (
      <li
        key={g.id}
        data-slot="priority"
        data-track={g.track}
        className="group/row flex flex-col gap-1"
      >
        <div className="flex min-w-0 items-center gap-2">
          <LensBandDot band={TRACK_BAND[g.track]} aria-label={TRACK_LABELS[g.track]} />
          {/* SEAT-DRAWER-CLICKS: the title opens the priority in place (edit, or read only). */}
          <button
            type="button"
            className="min-w-0 flex-1 truncate rounded-sm text-left text-sm leading-5 text-secondary hover:text-foreground hover:underline focus-visible:outline-2 focus-visible:outline-ring"
            title={g.title}
            data-slot="priority-open"
            onClick={() => setItem({ kind: "priority", goal: g })}
          >
            {g.title}
          </button>
          {g.milestones.length ? (
            <span className="shrink-0 text-xs text-faint tabular-nums">
              {done}/{g.milestones.length}
            </span>
          ) : null}
          {canWriteGoals ? (
            <>
              <NativeSelect
                size="sm"
                aria-label={`Track of ${g.title}`}
                value={g.track}
                onChange={(e) =>
                  patch.mutate({ id: g.id, body: { track: e.target.value as Track } })
                }
                className="w-28"
              >
                {TRACKS.map((t) => (
                  <NativeSelectOption key={t} value={t}>
                    {TRACK_LABELS[t]}
                  </NativeSelectOption>
                ))}
              </NativeSelect>
              <RowActions className="shrink-0">
                <RowAction
                  icon={PencilIcon}
                  label={`Edit ${g.title}`}
                  tip="Edit"
                  onClick={() => setItem({ kind: "priority", goal: g })}
                />
                <RowAction
                  icon={Trash2Icon}
                  label={`Remove ${g.title}`}
                  tip="Remove"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(g.id)}
                />
              </RowActions>
            </>
          ) : (
            <span className="shrink-0 text-xs text-faint">{TRACK_LABELS[g.track]}</span>
          )}
        </div>
        {g.milestones.length ? (
          <ul className="ml-4 flex flex-col gap-0.5" data-slot="milestones">
            {g.milestones.map((m) => (
              <li key={m.id} className="flex min-w-0 items-center gap-2 text-xs">
                <Checkbox
                  id={`${id}-${m.id}`}
                  checked={m.done_at !== null}
                  disabled={!canWriteGoals || patch.isPending}
                  onCheckedChange={(on) => toggleMilestone(g, m.id, on === true)}
                  aria-label={`${m.title}: ${m.done_at ? "done" : "not done"}`}
                />
                <label
                  htmlFor={`${id}-${m.id}`}
                  className={cn(
                    "min-w-0 flex-1 truncate",
                    m.done_at ? "text-faint line-through" : "text-secondary",
                  )}
                >
                  {m.title}
                </label>
              </li>
            ))}
            <li className="text-xs text-faint">
              {done} of {g.milestones.length} milestones done
            </li>
          </ul>
        ) : null}
      </li>
    );
  };

  // CHART-KB completeness: each missing item with the way to add it.
  const seatSearch = (tab: "kpis" | "money" | "recurring") => (prev: Record<string, unknown>) => ({
    ...keepFocus(prev),
    tab,
  });
  const actionFor = (item: NonNullable<SeatDimensions["completeness"]>["missing"][number]) => {
    const label = COMPLETENESS_ACTIONS[item];
    const cls = cn(buttonVariants({ variant: "outline", size: "xs" }), "shrink-0");
    switch (item) {
      case "holder":
        return manager ? (
          <span className="flex shrink-0 gap-1.5">
            {/* MODALS: Setup's seat drawer over the chart, not Setup › Seats. */}
            <Button variant="outline" size="xs" onClick={() => setQuick("holder")}>
              {label}
            </Button>
            {/* DE-WIZARD: an open seat filled by a digital employee (the one add flow). */}
            <Link
              to="/c/$slug/setup/des/new"
              params={{ slug: company.slug }}
              search={(prev: Record<string, unknown>) => ({ ...keepFocus(prev), seat: seat.id })}
              className={cls}
              data-slot="fill-with-de"
              data-page-link=""
            >
              Hire a DE here
              <PageArrow />
            </Link>
          </span>
        ) : null;
      case "reports_to":
        return (onReportsTo ?? onLines) ? (
          <Button variant="outline" size="xs" onClick={onReportsTo ?? onLines}>
            <GitBranchPlusIcon data-icon="inline-start" aria-hidden="true" />
            {label}
          </Button>
        ) : null;
      case "team":
        return manager ? (
          <Button variant="outline" size="xs" onClick={() => setQuick("team")}>
            <PlusIcon data-icon="inline-start" aria-hidden="true" />
            {label}
          </Button>
        ) : null;
      case "responsibility":
        return manager && seat.status !== "closed" ? (
          <Button variant="outline" size="xs" onClick={() => setQuick("responsibility")}>
            <PlusIcon data-icon="inline-start" aria-hidden="true" />
            {label}
          </Button>
        ) : null;
      case "measurable":
        return manager && kpiOn && seat.status !== "closed" ? (
          <Button variant="outline" size="xs" onClick={() => setAddingKpi("kpi")}>
            <PlusIcon data-icon="inline-start" aria-hidden="true" />
            {label}
          </Button>
        ) : manager ? (
          <Link
            to="/c/$slug/kpis/chart/seat/$id"
            params={{ slug: company.slug, id: seat.id }}
            search={seatSearch("kpis")}
            className={cls}
            data-page-link=""
          >
            {label}
            <PageArrow />
          </Link>
        ) : null;
      case "finance":
        return manager ? (
          <Button variant="outline" size="xs" onClick={() => setQuick("budget")}>
            <PlusIcon data-icon="inline-start" aria-hidden="true" />
            {label}
          </Button>
        ) : null;
      case "keystone":
        return canWriteGoals ? (
          <Button variant="outline" size="xs" onClick={() => setQuick("priority")}>
            <PlusIcon data-icon="inline-start" aria-hidden="true" />
            {label}
          </Button>
        ) : null;
    }
  };

  // The week's card failed (a viewer the chart shows the KPIs to, who may not read the seat's
  // scorecard): the chart's own summary rows, read-only, instead of a row model.
  const cardFailed = perfTrend.at(-1)?.isError === true;
  const summaryRows = (top: NonNullable<SeatDimensions["kpi"]>["top"]) =>
    top.length ? (
      <ul className="flex flex-col gap-1" data-slot="kpi-summary">
        {top.map((m) => {
          const h = lineHealth(m) ?? "none";
          return (
            <li key={m.metric_def_id} className="flex min-w-0 items-center gap-2 text-xs">
              <LensBandDot band={h} aria-label={HEALTH_LABEL[h]} />
              <span className="min-w-0 flex-1 truncate text-sm text-secondary" title={m.name}>
                {m.name}
              </span>
              <span className={cn("shrink-0 tabular-nums", HEALTH_TEXT[h])}>
                {formatCardMetric(m.actual, m.unit)}
                {m.target !== null ? (
                  <span className="text-faint"> / {formatCardMetric(m.target, m.unit)}</span>
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>
    ) : (
      <p className="text-xs text-faint">The seat's rows are shown to its managers and holder.</p>
    );

  // SEAT-DRAWER-UX §2: a Performance (KPI) or Execution (goal) row. The name opens the KPI in
  // place; a manager edits or removes it from the hover actions.
  const goalChoices = weekCard ? goalCandidates(weekCard.metrics) : [];
  // The header's second line names the role once: not again when it is the seat title or the
  // holder's own name.
  const sameText = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();
  const showRole =
    roleName.trim() !== "" &&
    !sameText(roleName, seat.title) &&
    !sameText(roleName, seatNameText(seat));
  const noNumbers = (rows: readonly Pick<ScorecardMetric, "actual" | "target">[]) =>
    rows.every((m) => m.actual === null && m.target === null);
  const metricRow = (m: ScorecardMetric, home: "performance" | "execution" | "record") => {
    const h = lineHealth(m) ?? "none";
    const weights = weightChip(m, home);
    return (
      <ItemRow
        key={m.metric_def_id}
        item={m.metric_def_id}
        slot={
          home === "performance"
            ? "measurable-link"
            : home === "execution"
              ? "execution-row"
              : "record-row"
        }
        title={m.name}
        name={m.name}
        onOpen={() => setItem({ kind: "measurable", id: m.metric_def_id, name: m.name, home })}
        dot={<LensBandDot band={h} aria-label={HEALTH_LABEL[h]} />}
        value={
          home !== "execution" ? (
            <span className={HEALTH_TEXT[h]}>
              {formatCardMetric(m.actual, m.unit)}
              {m.target !== null ? (
                <span className="text-faint"> / {formatCardMetric(m.target, m.unit)}</span>
              ) : null}
            </span>
          ) : (
            <span className="text-faint">
              {m.attainment === null ? "—" : `${Math.round(m.attainment * 100)}%`}
            </span>
          )
        }
        trend={{
          values: m.trend.map((p) => p.actual),
          reference: m.trend.map((p) => p.target),
          label: `${m.name}, last ${m.trend.length} weeks`,
        }}
        chip={
          <>
            {weights ? (
              <>
                <Badge variant="neutral" className="shrink-0" data-slot="weight-chip">
                  {weights}
                </Badge>
                <InfoTip label="About weight" className="size-4">
                  How much this KPI counts. KPI weight sets its share of the KPI score; goal weight
                  sets its share of the goal score. Default is 1.
                </InfoTip>
              </>
            ) : null}
            <AgentReportMark metric={m} />
            <ReportingStatusChip compact pageMark={false} {...reportingProps(m)} />
          </>
        }
        actions={
          canEditKpis ? (
            <>
              <RowAction
                icon={PencilIcon}
                label={`Edit ${m.name}`}
                tip="Edit"
                onClick={() =>
                  setItem({
                    kind: "measurable",
                    id: m.metric_def_id,
                    name: m.name,
                    home,
                    edit: true,
                  })
                }
              />
              <RowAction
                icon={Trash2Icon}
                label={`Remove ${m.name} from this seat`}
                tip="Remove from this seat (its history stays)"
                onClick={() => setRemoving({ id: m.metric_def_id, name: m.name })}
              />
            </>
          ) : undefined
        }
        below={
          home === "execution" ? (
            <InlineBar
              value={m.attainment === null ? 0 : Math.min(100, m.attainment * 100)}
              size="xs"
              tone={HEALTH_BAR[h]}
              aria-hidden="true"
            />
          ) : undefined
        }
      />
    );
  };

  const completeness = dims?.completeness;
  const seatRef = { id: seat.id, title: seat.title, status: seat.status };
  const quickDialogs = (
    <>
      {addingKpi ? (
        <AddKpiDialog
          api={api}
          company={company}
          seatId={seat.id}
          seatTitle={seat.title}
          holders={holdersOf(seat)}
          asGoal={addingKpi === "goal"}
          onClose={() => setAddingKpi(null)}
        />
      ) : null}
      {removing ? (
        <ConfirmRemove
          title={`Remove ${removing.name} from this seat?`}
          description="The metric and its history stay; the seat stops scoring it."
          confirmLabel="Remove"
          pending={removeKpi.isPending}
          onConfirm={() => removeKpi.mutate(removing.id)}
          onClose={() => setRemoving(null)}
        />
      ) : null}
      {pickingGoal && weekCard ? (
        <GoalPicker
          api={api}
          company={company}
          seat={seatRef}
          holders={holdersOf(seat)}
          choices={goalCandidates(weekCard.metrics)}
          onClose={() => setPickingGoal(false)}
        />
      ) : null}
      {quick === "responsibility" ? (
        <ResponsibilityDialog
          api={api}
          co={company.id}
          seatId={seat.id}
          seatTitle={seat.title}
          onClose={() => setQuick(null)}
        />
      ) : quick === "priority" ? (
        <PriorityDialog
          api={api}
          co={company.id}
          seatId={seat.id}
          seatTitle={seat.title}
          quarter={quarter}
          onClose={() => setQuick(null)}
        />
      ) : quick === "team" ? (
        <TeamMemberDialog
          api={api}
          co={company.id}
          of={{ kind: "seat", id: seat.id, title: seat.title }}
          onClose={() => setQuick(null)}
        />
      ) : quick === "budget" ? (
        <BudgetCreateDialog
          api={api}
          company={company}
          seat={{ id: seat.id, title: seat.title }}
          month={month}
          onClose={() => setQuick(null)}
        />
      ) : quick === "holder" ? (
        <EditSeatDrawer
          api={api}
          company={company}
          seatId={seat.id}
          onClose={() => setQuick(null)}
        />
      ) : null}
    </>
  );
  const closeSection = () => setSection(null);
  const sectionModal =
    section === "performance" ? (
      <PerformanceModal
        api={api}
        company={company}
        seat={seatRef}
        holders={holdersOf(seat)}
        week={week}
        manager={manager}
        onClose={closeSection}
      />
    ) : section === "execution" ? (
      <ExecutionModal
        api={api}
        company={company}
        seat={seatRef}
        holders={holdersOf(seat)}
        week={week}
        onClose={closeSection}
      />
    ) : section === "responsibilities" ? (
      <ResponsibilitiesModal
        api={api}
        company={company}
        seat={seatRef}
        kpiOn={kpiFeature.enabled}
        readOnly={!canConfigure.responsibilities}
        onClose={closeSection}
      />
    ) : section === "tasks" ? (
      <TasksModal
        api={api}
        company={company}
        seat={seatRef}
        onOpenTask={(taskId) => {
          setSection(null);
          openTask(taskId);
        }}
        onClose={closeSection}
      />
    ) : section === "keystones" ? (
      <PrioritiesModal
        api={api}
        company={company}
        seat={seatRef}
        quarter={quarter}
        goals={goals.data?.data}
        pending={goals.isPending && keystonesOn}
        error={goals.error}
        readOnly={!canConfigure.keystones}
        onClose={closeSection}
      />
    ) : section === "overall" ? (
      <OverallModal api={api} company={company} lenses={lenses} onClose={closeSection} />
    ) : null;
  // SEAT-DRAWER-CLICKS: the row's item, in place. Each save refreshes the chart's reads (and so
  // this drawer); the drawer stays open on its seat.
  const closeItem = () => setItem(null);
  const itemModal =
    item?.kind === "measurable" ? (
      <KpiDetailModal
        api={api}
        company={company}
        seat={seatRef}
        holders={holdersOf(seat)}
        week={week}
        metricId={item.id}
        name={item.name}
        canEdit={canEditKpis}
        startEditing={item.edit === true}
        focusTarget={item.home === "execution"}
        names={holderNamesById}
        onRemove={
          canEditKpis
            ? () => {
                const name = item.name;
                const metric = item.id;
                setItem(null);
                setRemoving({ id: metric, name });
              }
            : undefined
        }
        onReport={
          !manager && canConfigure.performance
            ? () => {
                setItem(null);
                setSection("performance");
              }
            : undefined
        }
        onClose={closeItem}
      />
    ) : item?.kind === "responsibility" ? (
      <ResponsibilityItemModal
        api={api}
        company={company}
        seat={seatRef}
        responsibilityId={item.id}
        summary={item.summary}
        canEdit={canConfigure.responsibilities}
        kpiOn={kpiFeature.enabled}
        onClose={closeItem}
      />
    ) : item?.kind === "priority" ? (
      canWriteGoals ? (
        <PriorityDialog
          api={api}
          co={company.id}
          seatId={seat.id}
          seatTitle={seat.title}
          quarter={quarter}
          current={item.goal}
          onClose={closeItem}
        />
      ) : (
        <PriorityDetailModal goal={item.goal} quarter={quarter} onClose={closeItem} />
      )
    ) : null;
  const body = (
    <div className="flex flex-col gap-3" data-slot="seat-panel-body">
      {quickDialogs}
      {sectionModal}
      {itemModal}
      <TaskDrawer
        api={api}
        company={company}
        taskId={task}
        onOpenTask={setTask}
        onClose={() => {
          closeTask();
          refresh();
          void qc.invalidateQueries({ queryKey: seatTasksKey(company.id, seat.id) });
        }}
      />
      <div className="flex items-start gap-3">
        <SeatAvatar seat={seat} size="lg" />
        <div className="flex min-w-0 flex-1 flex-col">
          <SeatTitle
            api={api}
            company={company}
            seatId={seat.id}
            title={seat.title}
            canEdit={manager && !closed}
          />
          <span
            className={cn(
              "text-sm leading-5 break-words",
              seat.holders.length === 0 ? "text-faint" : "text-muted-foreground",
            )}
          >
            {/* SEAT-DRAWER-CLICKS: the holder opens a popover in place (who holds the seat; a
                manager changes the holder from it), not a page. */}
            {seat.holders.length ? (
              <Popover>
                <PopoverTrigger
                  render={
                    <button
                      type="button"
                      className={NAME_BUTTON}
                      data-slot="panel-holder"
                      aria-label={`Holder: ${seatNameText(seat)}`}
                    />
                  }
                >
                  {seatNameText(seat)}
                </PopoverTrigger>
                <PopoverContent align="start" className="w-64 gap-2 p-3" data-slot="holder-popover">
                  <PopoverTitle className="text-sm">{seat.title}</PopoverTitle>
                  <ul className="flex flex-col gap-1.5">
                    {holdersOf(seat).map((h, i) => (
                      <li
                        key={seat.holders[i]?.membership_id ?? h.name}
                        className="flex min-w-0 items-center gap-1.5 text-sm"
                      >
                        <span className="min-w-0 truncate text-secondary">{h.name}</span>
                        {h.kind === "digital_employee" ? <WorkerBadge kind="de" size="sm" /> : null}
                        {/* SEAT-DRAWER-UX §3: a DE's own page, one marked click away. */}
                        {h.kind === "digital_employee" && seat.holders[i] ? (
                          <Link
                            to="/c/$slug/agents/de/$id"
                            params={{
                              slug: company.slug,
                              id: seat.holders[i]?.membership_id ?? "",
                            }}
                            search={keepFocus}
                            className="ml-auto shrink-0 text-xs text-primary-text hover:underline"
                            data-page-link=""
                            data-slot="holder-page"
                          >
                            Open
                            <PageArrow />
                          </Link>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                  {manager && !closed ? (
                    <Button
                      variant="outline"
                      size="xs"
                      className="self-start"
                      onClick={() => setQuick("holder")}
                    >
                      Change holder
                    </Button>
                  ) : null}
                </PopoverContent>
              </Popover>
            ) : (
              seatNameText(seat)
            )}
            {showRole ? <> · {roleName}</> : null}
          </span>
          {deHolder ? (
            <DefinitionLine company={company} deMembershipId={deHolder.membership_id} />
          ) : null}
          {/* DE-SEAT-OWNER: assist · act when vacant · own (the flag on; managers change it). */}
          {deHolder && !closed ? (
            <SeatDeFill api={api} company={company} seatId={seat.id} canEdit={manager} />
          ) : null}
          {/* SHARED-DES: the teams and seats this DE also serves (owners and admins edit). */}
          {deHolder && !closed ? (
            <ServesLine api={api} company={company} seatId={seat.id} chart={chart} />
          ) : null}
          {/* AWARENESS-MVP: the boards this DE is told to use (owners and admins). */}
          {deHolder && !closed && manager ? (
            <SeatBoardsLine api={api} company={company} seatId={seat.id} />
          ) : null}
          {/* SEAT-DRAWER-UX §3: an open seat's holder actions sit in the header. */}
          {seat.holders.length === 0 && manager && !closed ? (
            <span className="mt-1 flex flex-wrap items-center gap-1.5" data-slot="header-holder">
              {actionFor("holder")}
            </span>
          ) : null}
          <span className="mt-1.5 flex flex-wrap items-center gap-1">
            {composition !== "human" && composition !== "open" ? (
              <Badge variant="accent">{COMPOSITION_LABELS[composition]}</Badge>
            ) : null}
            {/* An open seat's holder line says "Open seat": neither chip repeats it. */}
            {seat.status !== "filled" && !(seat.status === "open" && composition === "open") ? (
              <Badge variant={SEAT_STATUS_TONE[seat.status]}>
                {SEAT_STATUS_LABELS[seat.status]}
              </Badge>
            ) : null}
          </span>
        </div>
        {/* SEAT-DRAWER-UX §3: the ring opens the setup checklist in a popover (each missing
            item with its add); at 100 % a small ✓ takes its place. */}
        {completeness && lenses.includes("completeness") ? (
          <Popover open={setupOpen} onOpenChange={setSetupOpen}>
            <PopoverTrigger
              render={
                <button
                  type="button"
                  className="flex shrink-0 flex-col items-center gap-0.5 rounded-md p-0.5 hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"
                  aria-label={`Seat setup ${completeness.score}% complete: ${completeness.met} of ${completeness.required} items in place. Open the checklist`}
                  data-slot="setup-ring"
                />
              }
            >
              {completeness.score >= 100 ? (
                <span className="flex items-center gap-1 text-xs text-success-text">
                  <CheckIcon aria-hidden="true" className="size-3.5" />
                  Set up
                </span>
              ) : (
                <>
                  <CompletenessRing
                    score={completeness.score}
                    band={completeness.score >= 80 ? "amber" : "red"}
                    size="lg"
                    className="text-[10px] tracking-tight"
                    label={`Seat setup ${completeness.score}% complete: ${completeness.met} of ${completeness.required} items in place`}
                  />
                  <span className="text-[10px] leading-3 text-faint">
                    Setup {completeness.score}%
                  </span>
                </>
              )}
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 gap-2 p-3" data-slot="setup-popover">
              <PopoverTitle className="flex items-center gap-1.5 text-sm">
                Seat setup
                <InfoTip label="About seat setup" className="size-4">
                  What a seat needs to count as set up. Setup › Chart chooses the items.
                </InfoTip>
              </PopoverTitle>
              <p className="text-xs text-muted-foreground">
                {completeness.met} of {plural(completeness.required, "required item")} in place
              </p>
              <SetupChecklist dims={dims} required={required} actionFor={actionFor} />
              {/* GUIDED-FLOW: the seat's open guided setup, when one was sent. */}
              <OpenSeatSetup api={api} company={company} seatId={seat.id} />
            </PopoverContent>
          </Popover>
        ) : null}
      </div>
      {fallback ? (
        <p className="flex items-center gap-1.5 text-xs text-faint" data-slot="panel-mode-fallback">
          <LockIcon aria-hidden="true" className="size-3 shrink-0" />
          {PANEL_LENS_FULL[fallback]} is not shown to you on this seat: all dimensions instead.
        </p>
      ) : null}
      {one ? null : (
        <dl className="flex flex-col gap-1" data-slot="seat-facts">
          <Fact label="Reports to">
            {/* SEAT-DRAWER-CLICKS: the supervisor's drawer opens in place of this one. */}
            {supervisor && openSeat ? (
              <button
                type="button"
                className={NAME_BUTTON}
                data-slot="panel-reports-to"
                title={`Open ${supervisor.title}`}
                onClick={() => openSeat(supervisor.id)}
              >
                {seatNameText(supervisor)}
              </button>
            ) : supervisor ? (
              seatNameText(supervisor)
            ) : supervisorId ? (
              titleOf(supervisorId)
            ) : (
              <span className="text-faint">Nobody (top of the chart)</span>
            )}
            {supervisor ? <span className="text-faint"> · {supervisor.title}</span> : null}
            {onReportsTo ? (
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Reports to…"
                title="Reports to…"
                className="ml-1 align-middle text-faint hover:text-foreground"
                data-slot="panel-reports-to-edit"
                onClick={onReportsTo}
              >
                <PencilIcon aria-hidden="true" />
              </Button>
            ) : null}
          </Fact>
          <Fact label="Direct reports">
            {reports && openSeat && directReports.length ? (
              // The reports, listed in a popover; each opens its drawer in place of this one.
              <Popover>
                <PopoverTrigger
                  render={
                    <button type="button" className={NAME_BUTTON} data-slot="panel-reports" />
                  }
                >
                  {plural(reports, "seat")}
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-64 gap-1 p-2"
                  data-slot="reports-popover"
                >
                  <PopoverTitle className="px-1 text-xs text-faint">
                    Reports to {seat.title}
                  </PopoverTitle>
                  <ul className="flex flex-col">
                    {directReports.map((r) => (
                      <li key={r.id}>
                        <button
                          type="button"
                          className={cn(ROW_BUTTON, "mx-0 w-full text-sm")}
                          onClick={() => openSeat(r.id)}
                        >
                          <span className="min-w-0 flex-1 truncate text-secondary">
                            {seatNameText(r)}
                          </span>
                          <span className="shrink-0 truncate text-xs text-faint">{r.title}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </PopoverContent>
              </Popover>
            ) : reports ? (
              plural(reports, "seat")
            ) : (
              <span className="text-faint">None</span>
            )}
          </Fact>
          <Fact label="Teams">
            {teams.length ? teams.join(", ") : <span className="text-faint">None</span>}
          </Fact>
          {outgoing.map((l) => (
            <Fact key={`${l.kind}-${l.to}`} label={LINE_VERBS[l.kind]}>
              {seatLink(l.to)}
            </Fact>
          ))}
          {incoming.map((l) => (
            <Fact
              key={`${l.kind}-${l.from}`}
              label={l.kind === "authority" ? "Approved by" : "Gets work from"}
            >
              {seatLink(l.from)}
            </Fact>
          ))}
        </dl>
      )}

      {/* GUIDED-FLOW: a manager sends the seat's guided setup to a member or an invitee. */}
      {manager && seat.status !== "closed" ? (
        <Button
          variant="outline"
          size="xs"
          className="self-start"
          data-slot="send-seat-setup"
          onClick={() => setSendingSetup(true)}
        >
          <SendIcon data-icon="inline-start" aria-hidden="true" />
          Send seat setup
        </Button>
      ) : null}
      {sendingSetup ? (
        <SendSeatSetupDialog
          api={api}
          company={company}
          seat={{ id: seat.id, title: seat.title }}
          onClose={() => setSendingSetup(false)}
        />
      ) : null}

      {/* CHART-KB: the seat's setup — what is in place, what is missing, how to add it. */}
      {/* CHART-PANEL: hidden once the seat is fully set up (the ring above says 100 %), unless
          Completeness is what the panel shows; the % is the ring's, not repeated here. */}
      {lenses.includes("completeness") &&
      (pending || completeness) &&
      showSeatSetup(completeness?.score, shown, lens) ? (
        <LensSection lens="completeness" title="Seat setup" current={current("completeness")}>
          {pending || !completeness ? (
            waiting
          ) : (
            <div className="flex flex-col gap-1.5" data-slot="completeness">
              <p className="text-xs text-muted-foreground">
                {completeness.met} of {plural(completeness.required, "required item")} in place
                {completeness.missing.length === 0 && completeness.hidden === 0
                  ? ". The seat is fully set up."
                  : "."}{" "}
                {/* SEAT-DRAWER-UX §2.7: the full checklist is the ring's popover. */}
                <button
                  type="button"
                  className="text-primary-text hover:underline"
                  onClick={() => setSetupOpen(true)}
                  data-slot="setup-fix"
                >
                  Checklist
                </button>
              </p>
              {completeness.missing.length ? (
                <ul className="flex flex-col gap-1" data-slot="missing-items">
                  {completeness.missing.map((item) => (
                    <li
                      key={item}
                      data-missing={item}
                      className="flex min-w-0 items-center gap-2 text-sm leading-5"
                    >
                      <LensBandDot band="red" aria-label="Missing" />
                      <span className="min-w-0 flex-1 truncate text-secondary">
                        {COMPLETENESS_LABELS[item]}
                      </span>
                      {actionFor(item)}
                    </li>
                  ))}
                </ul>
              ) : null}
              {completeness.hidden > 0 ? (
                <p className="flex items-center gap-1.5 text-xs text-faint">
                  <LockIcon aria-hidden="true" className="size-3" />
                  {plural(completeness.hidden, "missing item")} in a dimension shown to others.
                </p>
              ) : null}
            </div>
          )}
        </LensSection>
      ) : null}

      {/* SEAT-DRAWER-UX §2.7: Overall is the All view's first block (not a tab): its score,
          the per-lens parts, its weights behind the gear. */}
      {lenses.includes("overall") && shown === "all" ? (
        <LensSection
          lens="overall"
          title={LENS_LABELS.overall}
          score={scoreMark("overall")}
          current={current("overall")}
          gear={gearFor("overall", LENS_LABELS.overall)}
        >
          {pending ? (
            waiting
          ) : !dims?.overall ? (
            <ShownTo level={lensAudience("overall", visibility)} />
          ) : (
            <ul className="flex flex-col gap-0.5 text-xs" data-slot="overall-parts">
              {SCORED_LENSES.filter((p) => lenses.includes(p)).map((p) => {
                const sc = visible(p) ? lensScoreOf(p, seat, dims, thresholds) : null;
                const g = sc ? lensGoodness(p as ScoredLens, sc, thresholds) : null;
                return (
                  <li key={p} className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="w-28 shrink-0 text-faint">{LENS_LABELS[p]}</span>
                    {sc ? (
                      <>
                        <LensBandDot band={sc.band} />
                        <span className="tabular-nums text-secondary">
                          {sc.score === null ? (sc.note ?? "—") : `${sc.score}${LENS_UNITS[p]}`}
                        </span>
                        {g !== null && p === "finance" ? (
                          <span className="text-faint">→ {g} as goodness</span>
                        ) : null}
                        {one && g !== null ? (
                          <InlineBar
                            value={Math.max(0, Math.min(100, g))}
                            size="xs"
                            tone={
                              sc.band === "red"
                                ? "danger"
                                : sc.band === "amber"
                                  ? "warning"
                                  : "primary"
                            }
                            className="basis-full"
                            aria-hidden="true"
                          />
                        ) : null}
                      </>
                    ) : (
                      <span className="flex items-center gap-1 text-faint">
                        <LockIcon aria-hidden="true" className="size-3" /> hidden
                      </span>
                    )}
                  </li>
                );
              })}
              <li className="text-faint">
                {dims.overall.scored} of {dims.overall.total} weighted lenses scored.
              </li>
            </ul>
          )}
        </LensSection>
      ) : null}

      {lenses.includes("performance") && shows("performance") ? (
        <LensSection
          lens="performance"
          title={LENS_LABELS.performance}
          score={scoreMark("performance")}
          current={current("performance")}
          gear={gearFor("performance", LENS_LABELS.performance)}
          period={dims?.kpi ? weekRangeLabel(week) : undefined}
          add={
            dims?.kpi !== undefined && canEditKpis ? (
              <TabAdd label="Add KPI" onClick={() => setAddingKpi("kpi")} slot="add-kpi-tab" />
            ) : undefined
          }
        >
          {pending ? (
            waiting
          ) : !dims || dims.kpi === undefined ? (
            <ShownTo level={visibility.kpi} />
          ) : dims.kpi === null ? (
            <p className="text-xs text-faint">Not scored: the seat has no metrics.</p>
          ) : (
            <div className="flex flex-col gap-1.5">
              {/* SEAT-DRAWER-UX §2.1: the stat line, then the KPI-weighted rows (no record row,
                  no goal-only row); a row opens its KPI in place, the scorecard is the marked
                  link below. */}
              {kpiList && kpiList.length > 0 && noNumbers(kpiList) ? (
                <p className="text-xs text-faint" data-slot="no-numbers">
                  No numbers yet ·{" "}
                  <Link
                    to="/c/$slug/kpis/entry/numbers"
                    params={{ slug: company.slug }}
                    className="text-primary-text hover:underline"
                    data-page-link=""
                  >
                    Add numbers
                    <PageArrow />
                  </Link>
                </p>
              ) : (
                <dl className="grid grid-cols-3 gap-2 text-xs" data-slot="tab-stats">
                  <div>
                    <dt className="text-faint">KPI score</dt>
                    <dd className="font-medium tabular-nums">{formatScore(dims.kpi.kpi_score)}</dd>
                  </div>
                  <div>
                    <dt className="text-faint">Data entered</dt>
                    <dd className="font-medium tabular-nums">
                      {Math.round(dims.kpi.completeness * 100)}%
                    </dd>
                  </div>
                  <div>
                    <dt className="text-faint">Targets met</dt>
                    <dd className="font-medium tabular-nums">
                      {kpiList ? metOf(kpiList) : cardFailed ? "—" : "…"}
                    </dd>
                  </div>
                </dl>
              )}
              {!kpiList ? (
                cardFailed ? (
                  summaryRows(dims.kpi.top)
                ) : (
                  waiting
                )
              ) : kpiList.length === 0 ? (
                <TabEmpty
                  actions={
                    canEditKpis ? (
                      <TabAdd label="Add KPI" onClick={() => setAddingKpi("kpi")} />
                    ) : undefined
                  }
                >
                  <span data-slot="perf-empty">No KPIs on this seat.</span>
                </TabEmpty>
              ) : (
                <ul className="flex flex-col gap-1" data-slot="measurables">
                  {topOf(kpiList, !one).shown.map((m) => metricRow(m, "performance"))}
                </ul>
              )}
              <MoreRowsLink
                n={topOf(kpiList ?? [], !one).more}
                onClick={() => view.setMode("performance")}
              />
              {recordCounted > 0 ? (
                <p className="text-xs text-faint" data-slot="record-line">
                  +{plural(recordCounted, "agent-record metric")} count in this score
                  {available.includes("record") ? (
                    <>
                      {" · "}
                      <button
                        type="button"
                        className="text-primary-text hover:underline"
                        onClick={() => view.setMode("record")}
                        data-slot="to-record"
                      >
                        Record
                      </button>
                    </>
                  ) : null}
                </p>
              ) : null}
              {failureList.length > 0 ? (
                <div className="flex flex-col gap-1" data-slot="failure-kpis">
                  <p className="text-xs text-faint">Failures (not in the score yet)</p>
                  <ul className="flex flex-col gap-1">
                    {failureList.map((m) => metricRow(m, "performance"))}
                  </ul>
                </div>
              ) : null}
              <p className="text-xs text-faint">
                {plural(kpiList?.length ?? dims.kpi.metrics, "KPI")}
                {" · "}
                <ScorecardLink company={company} seatId={seat.id} />
              </p>
              {trendOf(perfValues, perfWeeks, "")}
            </div>
          )}
        </LensSection>
      ) : null}

      {lenses.includes("execution") && shows("execution") ? (
        <LensSection
          lens="execution"
          title={LENS_LABELS.execution}
          score={scoreMark("execution")}
          current={current("execution")}
          gear={gearFor("execution", LENS_LABELS.execution)}
          period={dims?.kpi ? weekRangeLabel(week) : undefined}
          add={
            dims?.kpi !== undefined && canEditKpis ? (
              <TabAdd label="Add goal" onClick={() => setAddingKpi("goal")} slot="add-goal-tab" />
            ) : undefined
          }
        >
          {pending ? (
            waiting
          ) : !dims || dims.kpi === undefined ? (
            <ShownTo level={visibility.kpi} />
          ) : dims.kpi === null ? (
            <p className="text-xs text-faint">Not scored: the seat has no metrics.</p>
          ) : (
            <div className="flex flex-col gap-1.5" data-slot="execution">
              {/* SEAT-DRAWER-UX §2.2: the goal-weighted rows (attainment toward each target). */}
              <dl className="grid grid-cols-3 gap-2 text-xs" data-slot="tab-stats">
                <div>
                  <dt className="text-faint">Goal score</dt>
                  <dd className="font-medium tabular-nums">{formatScore(dims.kpi.goal_score)}</dd>
                </div>
                <div>
                  <dt className="text-faint">Targets met</dt>
                  <dd className="font-medium tabular-nums">
                    {goalList ? metOf(goalList) : cardFailed ? "—" : "…"}
                  </dd>
                </div>
                <div>
                  <dt className="text-faint">Avg attainment</dt>
                  <dd className="font-medium tabular-nums">
                    {goalList ? meanAttainment(goalList) : cardFailed ? "—" : "…"}
                  </dd>
                </div>
              </dl>
              {!goalList ? (
                cardFailed ? (
                  <p className="text-xs text-faint">
                    The seat's goals are shown to its managers and holder.
                  </p>
                ) : (
                  waiting
                )
              ) : goalList.length === 0 ? (
                <TabEmpty
                  actions={
                    canEditKpis ? (
                      <>
                        <TabAdd label="Add goal" onClick={() => setAddingKpi("goal")} />
                        {goalChoices.length ? (
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={() => setPickingGoal(true)}
                            data-slot="weight-a-kpi"
                          >
                            Weight an existing KPI
                          </Button>
                        ) : null}
                      </>
                    ) : undefined
                  }
                >
                  <span data-slot="exec-empty">
                    No goals yet. A goal is a KPI with a goal weight: it counts toward how far the
                    seat is, not only hit or miss.
                  </span>
                </TabEmpty>
              ) : (
                <ul className="flex flex-col gap-1.5" data-slot="goals">
                  {topOf(goalList, !one).shown.map((m) => metricRow(m, "execution"))}
                </ul>
              )}
              <MoreRowsLink
                n={topOf(goalList ?? [], !one).more}
                onClick={() => view.setMode("execution")}
              />
              <p className="text-xs text-faint">
                <ScorecardLink company={company} seatId={seat.id} />
              </p>
            </div>
          )}
        </LensSection>
      ) : null}

      {shows("responsibilities") ? (
        <LensSection
          lens="responsibilities"
          title={LENS_LABELS.responsibilities}
          score={scoreMark("responsibilities")}
          current={current("responsibilities")}
          gear={gearFor("responsibilities", LENS_LABELS.responsibilities)}
          add={
            !pending && dims?.responsibilities?.count && canConfigure.responsibilities ? (
              <TabAdd label="Add" onClick={() => setQuick("responsibility")} slot="add-resp-tab" />
            ) : undefined
          }
        >
          {pending ? (
            waiting
          ) : dims?.responsibilities ? (
            dims.responsibilities.count === 0 ? (
              <TabEmpty
                actions={
                  canConfigure.responsibilities ? (
                    <TabAdd label="Add" onClick={() => setQuick("responsibility")} />
                  ) : undefined
                }
              >
                Nothing yet. What does this seat answer for?
              </TabEmpty>
            ) : (
              (() => {
                // SEAT-DRAWER-UX §2.3: the full list in the tab (a seat viewer's read: a manager
                // or the holder); the dimensions' heaviest five otherwise, and in the All view
                // the first four with "+N more" → the tab.
                const rows: (ResponsibilitySummary & { id: string })[] =
                  one && responsibilityRows
                    ? responsibilityRows.map((r) => ({
                        id: r.id,
                        name: r.name,
                        weight: r.weight,
                        measured: r.kpi_metric_ids.length > 0,
                        description: r.description,
                      }))
                    : dims.responsibilities.top.map((r) => ({
                        id: r.id,
                        name: r.name,
                        weight: r.weight,
                        measured: r.measured,
                      }));
                const { shown: list } = topOf(rows, !one);
                const more = dims.responsibilities.count - list.length;
                return (
                  <>
                    <ul className="flex flex-col gap-1" data-slot="responsibilities-list">
                      {list.map((r) => (
                        <ItemRow
                          key={r.id}
                          item={r.id}
                          slot="responsibility-row"
                          title={r.name}
                          name={r.name}
                          onOpen={() => setItem({ kind: "responsibility", id: r.id, summary: r })}
                          dot={
                            <LensBandDot
                              band={r.measured ? "green" : "none"}
                              aria-label={r.measured ? "Measured" : "No measurable"}
                            />
                          }
                          value={<span className="text-faint">×{Number(r.weight)}</span>}
                          actions={
                            canConfigure.responsibilities ? (
                              <RowAction
                                icon={PencilIcon}
                                label={`Edit ${r.name}`}
                                tip="Edit"
                                onClick={() =>
                                  setItem({ kind: "responsibility", id: r.id, summary: r })
                                }
                              />
                            ) : undefined
                          }
                          below={
                            r.description ? (
                              <span className="ml-4 line-clamp-1 text-xs break-words text-faint">
                                {r.description}
                              </span>
                            ) : undefined
                          }
                        />
                      ))}
                    </ul>
                    <p className="text-xs text-faint">
                      {dims.responsibilities.measurable} of {dims.responsibilities.count} with
                      measurables
                      {more > 0 ? (
                        <>
                          {" · "}
                          {/* The rest: the tab (its full list is a seat viewer's read: a
                              manager or the holder); anyone else sees the count. */}
                          {(manager || holder) && !one ? (
                            <button
                              type="button"
                              className="text-primary-text hover:underline"
                              onClick={() => view.setMode("responsibilities")}
                              data-slot="responsibilities-more"
                            >
                              +{more} more
                            </button>
                          ) : (
                            `+${more} more`
                          )}
                        </>
                      ) : null}
                    </p>
                  </>
                );
              })()
            )
          ) : null}
        </LensSection>
      ) : null}

      {lenses.includes("tasks") && shows("tasks") ? (
        <LensSection
          lens="tasks"
          title={LENS_LABELS.tasks}
          score={scoreMark("tasks")}
          current={current("tasks")}
          gear={gearFor("tasks", LENS_LABELS.tasks)}
          add={
            !pending && canConfigure.tasks ? (
              <TabAdd
                label={composition === "human" ? "New task" : "Give work"}
                onClick={() => setNewTask(true)}
                slot="add-task-tab"
              />
            ) : undefined
          }
        >
          {pending ? (
            waiting
          ) : !dims?.load ? (
            <ShownTo level={visibility.load} />
          ) : (
            <>
              <dl className="grid grid-cols-3 gap-2 text-xs">
                <div>
                  <dt className="text-faint">Open tasks</dt>
                  <dd className="font-medium tabular-nums">{dims.load.open}</dd>
                </div>
                <div>
                  <dt className="text-faint">Overdue</dt>
                  <dd
                    className={cn(
                      "font-medium tabular-nums",
                      dims.load.overdue > 0 && "text-warning-text",
                    )}
                  >
                    {dims.load.overdue}
                  </dd>
                </div>
                <div>
                  <dt className="text-faint">Estimated</dt>
                  <dd className="font-medium tabular-nums">
                    {hoursText(dims.load.estimate_minutes)} h
                  </dd>
                </div>
                {one ? (
                  <p className="col-span-3 text-faint" data-slot="tasks-detail">
                    {dims.load.open === 0
                      ? "No open tasks assigned to the seat."
                      : `${Math.round((dims.load.overdue / dims.load.open) * 100)}% overdue · about ${hoursText(
                          Math.round(dims.load.estimate_minutes / dims.load.open),
                        )} h per open task.`}
                  </p>
                ) : null}
              </dl>
              {/* SEAT-DRAWER-UX §2.4: a manager's or the holder's rows (each opening Work's task
                drawer), Done in 7 days, the last done; the Tasks modal (reassign) behind
                "View all". Anyone else reads the counts. */}
              {canConfigure.tasks ? (
                <>
                  <SeatTaskRows
                    api={api}
                    company={company}
                    seatId={seat.id}
                    all={!one}
                    onOpenTask={openTask}
                    onMore={() => view.setMode("tasks")}
                    empty={
                      <TabEmpty
                        actions={
                          <TabAdd
                            label={composition === "human" ? "New task" : "Give work"}
                            onClick={() => setNewTask(true)}
                          />
                        }
                      >
                        Nothing open on this seat.
                      </TabEmpty>
                    }
                  />
                  {dims.load.open > 0 ? (
                    <button
                      type="button"
                      className="self-end text-xs text-primary-text hover:underline"
                      onClick={() => setSection("tasks")}
                      data-slot="tasks-view-all"
                    >
                      View all
                    </button>
                  ) : null}
                  <SeatNewTask
                    api={api}
                    company={company}
                    seatId={seat.id}
                    open={newTask}
                    onOpenChange={setNewTask}
                  />
                </>
              ) : dims.load.open === 0 ? (
                <TabEmpty>Nothing open on this seat.</TabEmpty>
              ) : null}
            </>
          )}
        </LensSection>
      ) : null}

      {lenses.includes("finance") && served.includes("cost") && shows("finance") ? (
        <LensSection
          lens="finance"
          title={month ? `${LENS_LABELS.finance} · ${monthLabel(month)}` : LENS_LABELS.finance}
          score={scoreMark("finance")}
          current={current("finance")}
          add={
            !pending && canConfigure.finance ? (
              <TabAdd label="Add budget" onClick={() => setQuick("budget")} slot="add-budget-tab" />
            ) : undefined
          }
        >
          {pending ? (
            waiting
          ) : !dims || dims.cost === undefined ? (
            <ShownTo level={visibility.cost} />
          ) : (
            <div className="flex flex-col gap-2">
              {dims.cost === null ? (
                <p className="text-xs text-faint">No cost this month.</p>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm tabular-nums">
                    <span className="font-medium">
                      {formatMoney(dims.cost.actual_minor, dims.cost.currency)}
                    </span>
                    {dims.cost.budget_minor !== null ? (
                      <span className="text-muted-foreground">
                        of{" "}
                        {formatMoney(dims.cost.budget_minor, dims.cost.currency, { whole: true })} ·{" "}
                        {ratioText(dims.cost.utilization)}
                      </span>
                    ) : (
                      <span className="text-faint">no budget</span>
                    )}
                    {dims.cost.restricted_hidden ? <PayHiddenBadge /> : null}
                  </div>
                  {dims.cost.utilization !== null ? (
                    <InlineBar
                      value={Math.min(100, Number(dims.cost.utilization) * 100)}
                      size="sm"
                      tone={
                        budgetState(dims.cost).tone === "danger"
                          ? "danger"
                          : budgetState(dims.cost).tone === "warning"
                            ? "warning"
                            : "primary"
                      }
                      aria-hidden="true"
                    />
                  ) : null}
                  {one && dims.cost.budget_minor !== null ? (
                    <dl className="grid grid-cols-3 gap-2 text-xs" data-slot="finance-detail">
                      <div>
                        <dt className="text-faint">Budget</dt>
                        <dd className="font-medium tabular-nums">
                          {formatMoney(dims.cost.budget_minor, dims.cost.currency, { whole: true })}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-faint">Actual</dt>
                        <dd className="font-medium tabular-nums">
                          {formatMoney(dims.cost.actual_minor, dims.cost.currency, { whole: true })}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-faint">
                          {dims.cost.actual_minor > dims.cost.budget_minor ? "Over by" : "Left"}
                        </dt>
                        <dd
                          className={cn(
                            "font-medium tabular-nums",
                            dims.cost.actual_minor > dims.cost.budget_minor && "text-danger-text",
                          )}
                        >
                          {formatMoney(
                            Math.abs(dims.cost.budget_minor - dims.cost.actual_minor),
                            dims.cost.currency,
                            { whole: true },
                          )}
                        </dd>
                      </div>
                    </dl>
                  ) : null}
                  {trendOf(finValues, finMonths, "%")}
                </div>
              )}
              {/* DE-BUDGET-PACE: the seat's DE against its own limit and whether it lasts. */}
              {deHolder && (manager || seatViewer || dims.cost?.de_budget) ? (
                <DeBudgetPaceRow
                  api={api}
                  company={company}
                  deId={deHolder.membership_id}
                  deName={workerName(deHolder.display_name)}
                  summary={dims.cost?.de_budget}
                  currency={dims.cost?.currency ?? "USD"}
                  manager={manager}
                  reader={manager || seatViewer}
                />
              ) : null}
              {/* SEAT-DRAWER-UX §4.2: the seat's budgets (a manager's), spend, the pages. */}
              {canConfigure.finance ? (
                <SeatBudgets
                  api={api}
                  company={company}
                  seat={seatRef}
                  all={!one}
                  onMore={() => view.setMode("finance")}
                  empty={
                    <TabEmpty
                      actions={
                        <TabAdd
                          label="Add budget"
                          onClick={() => setQuick("budget")}
                          slot="add-budget-empty"
                        />
                      }
                    >
                      No budget on this seat: its spend counts against the levels above it.
                    </TabEmpty>
                  }
                />
              ) : null}
              {one && month && seatViewer ? (
                <SeatSpend
                  api={api}
                  company={company}
                  seatId={seat.id}
                  month={month}
                  deId={deHolder?.membership_id ?? null}
                />
              ) : null}
              <FinanceLinks
                company={company}
                manager={manager}
                deId={deHolder?.membership_id ?? null}
              />
            </div>
          )}
        </LensSection>
      ) : null}

      {lenses.includes("keystones") && shows("keystones") ? (
        <LensSection
          lens="keystones"
          title={quarter ? `${LENS_LABELS.keystones} · ${quarter}` : LENS_LABELS.keystones}
          score={scoreMark("keystones")}
          current={current("keystones")}
          gear={gearFor("keystones", LENS_LABELS.keystones)}
          add={
            !pending && dims?.keystones && canWriteGoals && goals.data?.data.length ? (
              <TabAdd
                label="Add a priority"
                onClick={() => setQuick("priority")}
                slot="add-priority-tab"
              />
            ) : undefined
          }
        >
          {pending ? (
            waiting
          ) : !dims?.keystones ? (
            <ShownTo level={visibility.keystones} />
          ) : (
            <div className="flex flex-col gap-2" data-slot="keystones">
              {goals.isPending && keystonesOn ? (
                waiting
              ) : goals.data?.data.length ? (
                <>
                  <ul className="flex flex-col gap-2">
                    {topOf(goals.data.data, !one).shown.map(priorityRow)}
                  </ul>
                  <MoreRowsLink
                    n={topOf(goals.data.data, !one).more}
                    onClick={() => view.setMode("keystones")}
                  />
                </>
              ) : (
                <TabEmpty
                  actions={
                    canWriteGoals ? (
                      <TabAdd label="Add a priority" onClick={() => setQuick("priority")} />
                    ) : undefined
                  }
                >
                  No priorities {quarter ? `for ${quarter}` : "this quarter"}.
                </TabEmpty>
              )}
              <p className="flex items-center gap-2 text-xs text-faint">
                {dims.keystones.count > 0 ? (
                  <span>
                    {dims.keystones.on} on track
                    {dims.keystones.at_risk ? ` · ${dims.keystones.at_risk} at risk` : ""}
                    {dims.keystones.off ? ` · ${dims.keystones.off} off track` : ""} of{" "}
                    {dims.keystones.count}.
                  </span>
                ) : null}
                {/* SEAT-DRAWER-CLICKS: the quarter's priorities in the Priorities modal (read
                    only where the viewer may not change them); KPIs › Priorities is linked
                    from it. */}
                <button
                  type="button"
                  className="ml-auto shrink-0 text-primary-text hover:underline"
                  data-slot="priorities-view-all"
                  onClick={() => setSection("keystones")}
                >
                  View all
                </button>
              </p>
              {goalError ? (
                <p role="alert" className="text-xs text-danger-text">
                  {goalError}
                </p>
              ) : null}
            </div>
          )}
        </LensSection>
      ) : null}

      {/* SEAT-DRAWER-UX §2.6: a DE-held seat's track record and its `de.*` rows. */}
      {deHolder && available.includes("record") && shows("record") ? (
        <LensSection
          lens="record"
          title="Record"
          current={false}
          period={pending ? undefined : "4 weeks"}
        >
          {pending ? (
            waiting
          ) : (
            <SeatRecord
              api={api}
              company={company}
              deId={deHolder.membership_id}
              rows={
                !weekCard ? null : recordRows(weekCard.metrics).length ? (
                  <>
                    <ul className="flex flex-col gap-1" data-slot="record-rows">
                      {topOf(recordRows(weekCard.metrics), !one).shown.map((m) =>
                        metricRow(m, "record"),
                      )}
                    </ul>
                    <MoreRowsLink
                      n={topOf(recordRows(weekCard.metrics), !one).more}
                      onClick={() => view.setMode("record")}
                    />
                  </>
                ) : null
              }
            />
          )}
        </LensSection>
      ) : null}

      {/* BIZ-PLAYBOOKS: the SOPs this seat owns or follows, with their followed %. */}
      {shown === "all" && !pending ? (
        <SeatPlaybooks api={api} company={company} seatId={seat.id} />
      ) : null}

      <div className="flex items-center gap-1.5 border-t border-subtle pt-3">
        <Link
          to="/c/$slug/kpis/chart/seat/$id"
          params={{ slug: company.slug, id: seat.id }}
          search={keepFocus}
          className={cn(buttonVariants({ variant: "outline", size: "sm" }))}
          data-slot="open-seat-page"
          data-page-link=""
        >
          Open the seat page
          <ArrowUpRightIcon data-icon="inline-end" aria-hidden="true" data-slot="page-arrow" />
        </Link>
        <Button
          variant="ghost"
          size="sm"
          aria-pressed={focused}
          className="aria-pressed:bg-primary-soft aria-pressed:text-primary-text"
          onClick={onFocus}
        >
          <CrosshairIcon data-icon="inline-start" aria-hidden="true" />
          {focused ? "Unfocus" : "Focus"}
        </Button>
      </div>
    </div>
  );

  const panelLabel = seatHolderLabel(seat.title, holderNames(seat));
  return (
    // The design spec's side panel (`SidePanel` in packages/ui: --card, a hairline on the left,
    // a 40 px header, a scrolling body, full-screen on phones), drawn here because this one's
    // header carries the view controls and its width is the viewer's.
    <aside
      id="seat-panel"
      aria-label={panelLabel}
      data-slot="side-panel"
      data-panel-mode={view.mode}
      data-panel-shown={shown}
      data-pinned={view.pinned ? "" : undefined}
      ref={asideRef}
      onKeyDown={onPanelKey}
      className={cn(
        "relative flex h-full w-(--seat-panel-w) shrink-0 flex-col border-l border-border bg-card text-card-foreground",
        "max-md:fixed max-md:inset-x-0 max-md:top-0 max-md:bottom-14 max-md:z-drawer max-md:w-full max-md:border-l-0",
      )}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: a focusable window splitter (WAI-ARIA separator); <hr> cannot take focus or pointer drags. */}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize the panel"
        aria-valuemin={PANEL_WIDTH.min}
        aria-valuemax={PANEL_WIDTH.max}
        aria-valuenow={width}
        tabIndex={0}
        data-slot="panel-resize"
        title="Drag to resize · double-click for the default width"
        onPointerDown={onDragStart}
        onPointerMove={onDragMove}
        onPointerUp={onDragEnd}
        onPointerCancel={onDragEnd}
        onKeyDown={onEdgeKey}
        onDoubleClick={() => {
          setWidth(clampPanelWidth(PANEL_WIDTH.default, window.innerWidth));
          writePanelWidth(me.user.id, PANEL_WIDTH.default);
        }}
        className={cn(
          "absolute inset-y-0 -left-1 z-10 w-2 cursor-col-resize touch-none max-md:hidden",
          "after:absolute after:inset-y-0 after:left-[3px] after:w-0.5 after:bg-transparent after:transition-colors hover:after:bg-primary/60 active:after:bg-primary",
          FOCUS_INSET,
        )}
      />
      <div
        className="flex shrink-0 flex-col gap-1.5 border-b border-border px-3 py-2"
        data-slot="seat-panel-header"
      >
        <div className="flex items-center gap-1.5">
          <NativeSelect
            size="sm"
            aria-label="The panel shows"
            value={view.mode}
            onChange={(e) => view.setMode(e.target.value as PanelMode)}
            className="min-w-0 flex-1"
            data-slot="panel-mode"
          >
            <NativeSelectOption value="all">All dimensions</NativeSelectOption>
            <NativeSelectOption value="follow">
              Follow chart tab ({lens === "structure" ? "all" : LENS_LABELS[lens]})
            </NativeSelectOption>
            {available.map((l) => (
              <NativeSelectOption key={l} value={l}>
                {PANEL_LENS_FULL[l]}
              </NativeSelectOption>
            ))}
            {fallback ? (
              <NativeSelectOption value={fallback} disabled>
                {PANEL_LENS_FULL[fallback]} (not shown on this seat)
              </NativeSelectOption>
            ) : null}
          </NativeSelect>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-pressed={view.pinned}
            aria-label={view.pinned ? "Unpin this view" : "Pin this view"}
            title={
              view.pinned
                ? "Pinned: the panel keeps this view for every seat"
                : "Pin: keep this view for every seat you open"
            }
            className="pointer-coarse:size-11 aria-pressed:bg-primary-soft aria-pressed:text-primary-text"
            onClick={view.togglePin}
            data-slot="panel-pin"
          >
            <PinIcon aria-hidden="true" className={cn(view.pinned && "fill-current")} />
          </Button>
          <Button
            variant="ghost"
            size="icon-sm"
            className="pointer-coarse:size-11"
            aria-label={`Close ${panelLabel.toLowerCase()}`}
            onClick={onClose}
          >
            <XIcon aria-hidden="true" />
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="Previous dimension"
            onClick={() => step(-1)}
          >
            <ChevronLeftIcon aria-hidden="true" />
          </Button>
          <div
            role="radiogroup"
            aria-label="Dimension"
            data-slot="panel-lens-strip"
            className="flex min-w-0 flex-1 items-center justify-between gap-0.5 strip-scroll"
          >
            {(["all", ...available] as PanelShown[]).map((v) => {
              const on = shown === v;
              // W10: every segment is labelled (a short name over a bar in the lens's hue); the
              // full name is its tooltip and accessible name. One line at the 440 px default.
              const label = v === "all" ? "All dimensions" : PANEL_LENS_FULL[v];
              return (
                // biome-ignore lint/a11y/useSemanticElements: a segment with the lens's swatch (as the chart's own lens tabs); the strip is the radiogroup.
                <button
                  key={v}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  aria-label={label}
                  title={label}
                  data-slot="panel-lens-tab"
                  data-value={v}
                  data-lens={v === "all" ? "structure" : v}
                  onClick={() => view.setMode(v)}
                  className={cn(
                    "relative inline-flex h-6 shrink-0 items-center rounded-sm px-1 text-[11px] leading-4 font-medium whitespace-nowrap text-muted-foreground transition-colors duration-(--dur-fast) hover:bg-sunken hover:text-foreground",
                    on && "bg-sunken text-foreground",
                    FOCUS_INSET,
                  )}
                >
                  {v === "all" ? "All" : PANEL_LENS_SHORT[v]}
                  <span
                    aria-hidden="true"
                    data-slot="panel-lens-swatch"
                    className={cn(
                      "absolute inset-x-1 bottom-0 h-0.5 rounded-full bg-(--lens)",
                      !on && "opacity-50",
                    )}
                  />
                </button>
              );
            })}
          </div>
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label="Next dimension"
            onClick={() => step(1)}
          >
            <ChevronRightIcon aria-hidden="true" />
          </Button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4 scrollbar-gutter-stable">{body}</div>
    </aside>
  );
}

/** The design system's inset focus ring (packages/ui `FOCUS_RING_INSET`). */
const FOCUS_INSET =
  "outline-none focus-visible:outline-2 focus-visible:outline-solid focus-visible:-outline-offset-2 focus-visible:outline-ring";

/** The band a roll-up reads with (re-exported for the chart's tests). */
export const rollupBand = bandOf;
